import { z } from "zod/v4";

import type { JsonObject } from "~/common/types/graphql.scalars";
import type { PropertySchemaAssociation_CategoryFragment } from "~/features/admin/categories";
import type { PropertySchemaAssociation_DefaultFragment } from "~/features/admin/propertySchemas";
import type { PropertySchemaAssociationFormData } from "~/features/dataImportProjects/types";

export const zDataSourceID = z.uuid().brand("DataSourceID");
type DataSourceID = z.infer<typeof zDataSourceID>;

export const zCategoryID = z.uuid().brand("CategoryID");
export type CategoryID = z.infer<typeof zCategoryID>;

/**
 * Configuration for foreign key column references
 */
export type ForeignKeyReference = {
  /** Name of the sheet containing the referenced data */
  sheetName: string;
  /** Name of the column being referenced (usually '_UUID') */
  columnLabel: string;
};

// Represents the state of a whole sheet
export interface SheetState {
  dataSourceID?: DataSourceID;
  readonly name: string; // sheet name should be unique within the spreadsheet
  readonly category: SchemaDesignerCategoryInstance;
  readonly kind: SheetKind;
  columns: SheetColumn[];
  order: number;
}

// Sheet kinds used in the UI
type SheetKind = "main" | "relation" | "detail";

export type ColumnKind = "data" | "foreignKey" | "custom";

type SheetColumnBase = {
  // Fields with the same names as ColumnPlan
  readonly columnId: string;
  readonly columnLabel: string;
  readonly columnPath: string;

  // Category information (for preview/edit)
  readonly categoryId: CategoryID | null; // null for custom columns
  readonly categoryName: string | null;
  readonly categoryInstanceId: string | null;
  readonly categoryInstanceLabel: string | null;

  // Display/interaction metadata
  readonly sheetName: string;
  readonly order: number;

  // Additional info required by current UI for type editing (property.type)
  readonly property: PropertySchemaAssociationFormData;
};

type SheetColumnData = SheetColumnBase & {
  readonly columnKind: "data" | "custom";
  readonly fkRef?: undefined;
};

type SheetColumnForeignKey = SheetColumnBase & {
  readonly columnKind: "foreignKey";
  readonly fkRef: ForeignKeyReference;
};

export type SheetColumn = SheetColumnData | SheetColumnForeignKey;

type SchemaDesignerCategoryBase = {
  id: CategoryID;
  name: string;
  i18nLabel?: JsonObject | null;
  properties: PropertySchemaAssociation_CategoryFragment[];
  metadata: PropertySchemaAssociation_DefaultFragment[];
};

export type SchemaDesignerEntityCategory = SchemaDesignerCategoryBase & {
  type: "entity";
};

export type SchemaDesignerSourceTargetCategory = {
  id: CategoryID;
  name: string;
  i18nLabel?: JsonObject | null;
};

export type SchemaDesignerRelationCategory = SchemaDesignerCategoryBase & {
  type: "relation";
  relationType: {
    sourceCategory: SchemaDesignerSourceTargetCategory;
    targetCategory: SchemaDesignerSourceTargetCategory;
  };
};

export type SchemaDesignerCategory = SchemaDesignerEntityCategory | SchemaDesignerRelationCategory;

type SchemaDesignerCategoryInstanceBase<TCategory extends SchemaDesignerCategory> = {
  instanceId: string; // Unique ID for this instance
  instanceLabel: string; // User-editable label (e.g., "actor1", "mainActor", etc.)
  isMain: boolean; // Flag to indicate if this is the main entity
  category: TCategory;
};

export type EntityCategoryInstance = SchemaDesignerCategoryInstanceBase<SchemaDesignerEntityCategory>;

export type RelationCategoryInstance = SchemaDesignerCategoryInstanceBase<SchemaDesignerRelationCategory> & {
  cardinality: SchemaDesignerCategoryCardinality;
  sourceCategory?: EntityCategoryInstance;
  targetCategory?: EntityCategoryInstance;
};

export type SchemaDesignerCategoryInstance = EntityCategoryInstance | RelationCategoryInstance;

// source:target
export const schemaDesignerCategoryCardinalities = {
  ONE_TO_ONE: "ONE_TO_ONE",
  ONE_TO_MANY: "ONE_TO_MANY",
  MANY_TO_ONE: "MANY_TO_ONE",
  MANY_TO_MANY: "MANY_TO_MANY",
} as const;

export type SchemaDesignerCategoryCardinality =
  (typeof schemaDesignerCategoryCardinalities)[keyof typeof schemaDesignerCategoryCardinalities];
